import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionLayer } from '@adornis/baseql/server/collections.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { debounceTime, switchMap } from 'rxjs';
import { getCurrentUserID } from '../../../api/currentUserID.js';
import { ManagedUser } from '../db/ManagedUser.js';
import { USER_ADMIN_ROLE } from '../db/roles.js';
import { assertUserMayManageUsers } from '../server/access.js';

/** Applied whenever the DataView carries no sorting of its own. */
const DEFAULT_SORT = { createdAt: -1 } as const;

/** The only fields that may leave the server — notably not `password`. */
const PROJECTION = { _id: 1, username: 1, email: 1, createdAt: 1, archivedAt: 1, canManageUsers: 1 } as const;

/**
 * Feeds the DataView. `canManageUsers` is derived from the embedded `roles` array so it can
 * be filtered and sorted like a real field — no join, the roles live on the user document.
 */
export const subscribeManagedUsers = registerSubscription({
  type: () => [ManagedUser],
  operationName: 'subscribeManagedUsers',
  resolve: (skip: number, limit: number, filter?: AdornisFilter) => {
    return (_gqlFields: BaseQLSelectionSet<ManagedUser>) => {
      // Captured while the request context is still there — the switchMap runs detached.
      const userID = getCurrentUserID();

      return watchEntireCollection(AdornisUser._collectionName).pipe(
        debounceTime(100),
        switchMap(async () => {
          await assertUserMayManageUsers(userID);

          const users = await getCollectionLayer<ManagedUser>(AdornisUser._collectionName);
          const sort = filter?.getMongoOptions().sort ?? {};
          const pipeline: any[] = [
            { $addFields: { canManageUsers: { $in: [USER_ADMIN_ROLE, { $ifNull: ['$roles.name', []] }] } } },
            { $match: filter?.getMongoFilter() ?? {} },
            // Not getMongoAggregationFilter(): it emits `$sort: {}` when nothing is set,
            // which mongo rejects — and this is where the createdAt default belongs.
            { $sort: Object.keys(sort).length ? sort : DEFAULT_SORT },
          ];
          if (skip > 0) pipeline.push({ $skip: skip });
          if (limit > 0) pipeline.push({ $limit: limit });
          pipeline.push({ $project: PROJECTION });

          const docs = await users.aggregate(pipeline).toArray();
          for (const doc of docs) (doc as any)._class = ManagedUser._class;
          return docs;
        }),
      );
    };
  },
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
});
